import React, { useEffect, useState } from "react";
import { X, ShieldCheck, Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function PermissionsModal({ user, onClose, onSaved }) {
  const { user: actor } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [extras, setExtras] = useState(new Set(user.extra_permissions || []));
  const [revokes, setRevokes] = useState(new Set(user.revoked_permissions || []));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/permissions/catalog").then((r) => setCatalog(r.data)).catch(() => {
      toast.error("Failed to load permissions catalog");
    });
  }, []);

  if (!catalog) {
    return (
      <ModalShell onClose={onClose} title="Manage permissions">
        <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>
      </ModalShell>
    );
  }

  const roleDefaults = new Set(catalog.role_defaults[user.role] || []);
  const privileged = new Set(catalog.privileged || []);
  const isSuper = actor?.role === "super_admin";

  const effective = (name) => (roleDefaults.has(name) || extras.has(name)) && !revokes.has(name);

  const togglePerm = (name) => {
    const inRole = roleDefaults.has(name);
    // Disallow modifying privileged perms unless actor is super_admin.
    if (privileged.has(name) && !isSuper) return;

    if (inRole) {
      // Toggle revoke
      const next = new Set(revokes);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      setRevokes(next);
    } else {
      // Toggle extra
      const next = new Set(extras);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      setExtras(next);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/users/${user.id}/permissions`, {
        extra_permissions: [...extras],
        revoked_permissions: [...revokes],
      });
      toast.success("Permissions updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      onClose={onClose}
      title={`Permissions for ${user.name}`}
      subtitle={user.email}
      wide
    >
      <div className="text-xs text-slate-500 mb-4 leading-relaxed">
        Defaults come from the role <strong className="text-slate-700">{user.role}</strong>.
        Click to toggle. <span className="text-orange-600">Orange</span> = granted on top of the
        role. <span className="text-rose-600">Red strikethrough</span> = revoked from the role.
        {!isSuper && (
          <span className="block mt-1 text-amber-700">
            Some privileged permissions are reserved for the Super Admin and shown disabled.
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[55vh] overflow-y-auto pr-1">
        {catalog.permissions.map((p) => {
          const name = p.name;
          const inRole = roleDefaults.has(name);
          const granted = effective(name);
          const isExtra = !inRole && extras.has(name);
          const isRevoked = inRole && revokes.has(name);
          const disabled = privileged.has(name) && !isSuper;

          let cls =
            "flex items-start gap-2.5 p-3 rounded-lg border text-left transition cursor-pointer ";
          if (disabled) {
            cls += "border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed";
          } else if (isExtra) {
            cls += "border-orange-200 bg-orange-50";
          } else if (isRevoked) {
            cls += "border-rose-200 bg-rose-50";
          } else if (granted) {
            cls += "border-emerald-200 bg-emerald-50/40";
          } else {
            cls += "border-slate-200 bg-white hover:border-orange-300";
          }

          return (
            <button
              type="button"
              key={name}
              disabled={disabled}
              onClick={() => togglePerm(name)}
              data-testid={`perm-toggle-${name}`}
              className={cls}
            >
              <input
                type="checkbox"
                checked={granted}
                readOnly
                className="mt-0.5 rounded border-slate-300 text-orange-500"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span
                    className={`font-mono text-[12px] font-semibold ${
                      isRevoked ? "text-rose-700 line-through" : "text-slate-800"
                    }`}
                  >
                    {name}
                  </span>
                  {inRole && (
                    <span className="px-1 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider bg-slate-200 text-slate-600">
                      role default
                    </span>
                  )}
                  {privileged.has(name) && (
                    <span className="px-1 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider bg-amber-100 text-amber-700">
                      super only
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">{p.description}</div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-end gap-2">
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100"
        >
          Cancel
        </button>
        <button
          data-testid="perms-save-btn"
          onClick={save}
          disabled={busy}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:bg-slate-200 text-white text-sm font-semibold"
        >
          <Save className="w-4 h-4" />
          {busy ? "Saving…" : "Save Permissions"}
        </button>
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, subtitle, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm overflow-y-auto">
      <div
        className={`bg-white rounded-2xl shadow-2xl w-full p-6 my-8 ${wide ? "max-w-3xl" : "max-w-md"}`}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              <ShieldCheck className="w-5 h-5 text-orange-500" />
              {title}
            </h3>
            {subtitle && <div className="text-xs text-slate-500 mt-0.5 font-mono">{subtitle}</div>}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
